// DeleteAccountModal.jsx — Suppression de compte in-app (Apple 5.1.1(v) + RGPD).
// Un président doit d'abord nommer un successeur ; re-saisie du mot de passe requise.
import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

export default function DeleteAccountModal({ onClose }) {
  const { logout } = useAuth();
  const [preview, setPreview] = useState(null);
  const [password, setPassword] = useState('');
  const [successor, setSuccessor] = useState('');
  const [ack, setAck] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/account/deletion-preview')
      .then(({ data }) => { if (alive) setPreview(data); })
      .catch(() => { if (alive) setPreview({ requires_successor: false, eligible_successors: [] }); });
    return () => { alive = false; };
  }, []);

  const requiresSuccessor = preview?.requires_successor;

  const submit = async () => {
    if (!password) return toast.error('Entrez votre mot de passe pour confirmer');
    if (requiresSuccessor && !successor) return toast.error('Choisissez le nouveau président');
    if (!ack) return toast.error('Cochez la case de confirmation');
    setLoading(true);
    try {
      await api.post('/account/delete', {
        mot_de_passe: password,
        nouveau_president_id: successor || null,
      });
      toast.success('Votre compte a été supprimé.');
      setTimeout(() => logout(), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la suppression');
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-[#0F172A]/60 backdrop-blur-md grid place-items-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="delete-account-modal"
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md my-6 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-7 sm:p-8">
          <div className="flex items-start justify-between mb-5">
            <div className="w-12 h-12 rounded-2xl bg-[#FEF2F2] grid place-items-center">
              <AlertTriangle className="w-6 h-6 text-[#DC2626]" />
            </div>
            <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]" aria-label="Fermer" data-testid="delete-account-cancel">
              <X className="w-5 h-5" />
            </button>
          </div>

          <h2 className="font-display text-2xl font-bold text-[#0F172A] mb-2">Supprimer mon compte</h2>
          <p className="text-sm text-[#475569] leading-relaxed mb-5">
            Cette action est <strong className="text-[#0F172A]">définitive</strong>. Votre accès et vos données personnelles
            (email, fiche annuaire, favoris) seront supprimés. Les archives de l'association
            (adhésions, présences, dons, événements) sont conservées pour l'historique.
          </p>

          {!preview ? (
            <div className="py-6 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>
          ) : (
            <div className="space-y-4">
              {requiresSuccessor && (
                <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-xl p-4">
                  <div className="text-sm font-bold text-[#9A3412] mb-2">
                    Vous êtes président·e — nommez un successeur
                  </div>
                  <p className="text-xs text-[#9A3412]/90 mb-3">
                    L'association doit garder un·e président·e. Choisissez le membre qui reprendra la présidence.
                  </p>
                  <select
                    value={successor}
                    onChange={(e) => setSuccessor(e.target.value)}
                    className="ap-input"
                    data-testid="delete-account-successor"
                  >
                    <option value="">— Choisir un nouveau président —</option>
                    {preview.eligible_successors.map((s) => (
                      <option key={s.id} value={s.id}>
                        {(s.prenom || '') + ' ' + (s.nom || '')} {s.email ? `(${s.email})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {preview.is_sole_member && (
                <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-xl p-3 text-xs text-[#9A3412]">
                  Vous êtes le seul membre : l'association sera archivée (données conservées).
                </div>
              )}

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">
                  Confirmez avec votre mot de passe
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="ap-input"
                  placeholder="••••••••"
                  data-testid="delete-account-password"
                />
              </div>

              <label className="flex items-start gap-2 text-xs text-[#475569] cursor-pointer">
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" data-testid="delete-account-ack" />
                <span>Je comprends que la suppression de mon compte est définitive.</span>
              </label>

              <div className="flex gap-2 justify-end pt-1">
                <button
                  onClick={onClose}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold text-[#475569] hover:bg-[#F1F5F9]"
                >
                  Annuler
                </button>
                <button
                  onClick={submit}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold disabled:opacity-50"
                  data-testid="delete-account-confirm"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Supprimer définitivement
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
